import { create } from 'zustand';
import { amlApi } from '../services/amlApi';
import type {
  SanctionsListStatus, SanctionsListType, ScreeningRun, ScreeningResult,
  ScreeningResultType, ScreeningReviewStatus,
} from '../types/aml';

/**
 * State for the OFAC sanctions screening section.
 *
 * List refreshes and screening runs are executed server-side in the
 * background, so both are followed with short polling until the backend
 * reports a terminal status.
 */

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

interface SanctionsState {
  lists: SanctionsListStatus[];
  listsLoading: boolean;
  refreshing: boolean;

  runs: ScreeningRun[];
  runsTotal: number;
  runsPage: number;
  runsLoading: boolean;
  screening: boolean;

  activeRun: ScreeningRun | null;
  results: ScreeningResult[];
  resultsTotal: number;
  resultsPage: number;
  resultsPageSize: number;
  resultsLoading: boolean;
  resultFilter: ScreeningResultType | 'all';
  reviewFilter: ScreeningReviewStatus | 'all';
  resultsSearch: string;

  error: string | null;

  fetchLists: () => Promise<void>;
  refreshLists: (listType?: SanctionsListType) => Promise<void>;

  fetchRuns: (page?: number) => Promise<void>;
  startScreening: (threshold?: number) => Promise<void>;

  openRun: (runId: string) => Promise<void>;
  closeRun: () => void;
  fetchResults: (page?: number) => Promise<void>;
  setResultFilter: (value: ScreeningResultType | 'all') => void;
  setReviewFilter: (value: ScreeningReviewStatus | 'all') => void;
  setResultsSearch: (value: string) => void;
  setResultsPageSize: (size: number) => void;
  reviewResult: (
    resultId: string, status: ScreeningReviewStatus, notes?: string | null,
  ) => Promise<void>;
}

const isListBusy = (lists: SanctionsListStatus[]) =>
  lists.some((l) => l.status === 'running');

export const useSanctionsStore = create<SanctionsState>((set, get) => ({
  lists: [],
  listsLoading: false,
  refreshing: false,

  runs: [],
  runsTotal: 0,
  runsPage: 1,
  runsLoading: false,
  screening: false,

  activeRun: null,
  results: [],
  resultsTotal: 0,
  resultsPage: 1,
  resultsPageSize: 50,
  resultsLoading: false,
  resultFilter: 'all',
  reviewFilter: 'all',
  resultsSearch: '',

  error: null,

  fetchLists: async () => {
    set({ listsLoading: true });
    try {
      const { lists } = await amlApi.getSanctionsLists();
      set({ lists, listsLoading: false, refreshing: isListBusy(lists) });
    } catch (e: any) {
      set({
        listsLoading: false,
        error: e?.response?.data?.detail || 'Failed to load sanctions lists',
      });
    }
  },

  refreshLists: async (listType) => {
    set({ refreshing: true, error: null });
    try {
      await amlApi.refreshSanctionsLists(listType);
    } catch (e: any) {
      set({
        refreshing: false,
        error: e?.response?.data?.detail || 'Failed to start the list refresh',
      });
      return;
    }

    const deadline = Date.now() + POLL_TIMEOUT_MS;
    // The download runs in a server background task, so poll for completion.
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      try {
        const { lists } = await amlApi.getSanctionsLists();
        set({ lists });
        if (!isListBusy(lists)) break;
      } catch {
        break;
      }
    }
    set({ refreshing: false });
  },

  fetchRuns: async (page) => {
    const nextPage = page ?? get().runsPage;
    set({ runsLoading: true });
    try {
      const { items, total } = await amlApi.listScreeningRuns({
        page: nextPage, page_size: 20,
      });
      set({ runs: items, runsTotal: total, runsPage: nextPage, runsLoading: false });
    } catch (e: any) {
      set({
        runsLoading: false,
        error: e?.response?.data?.detail || 'Failed to load screening history',
      });
    }
  },

  startScreening: async (threshold = 80) => {
    set({ screening: true, error: null });
    let runId: string;
    try {
      const run = await amlApi.startScreeningRun(threshold);
      runId = run.id;
      set({ runs: [run, ...get().runs], runsTotal: get().runsTotal + 1 });
    } catch (e: any) {
      set({
        screening: false,
        error: e?.response?.data?.detail || 'Failed to start screening',
      });
      return;
    }

    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      try {
        const run = await amlApi.getScreeningRun(runId);
        set({
          runs: get().runs.map((r) => (r.id === run.id ? run : r)),
        });
        if (run.status !== 'running') {
          if (run.status === 'failed') {
            set({ error: run.error || 'Screening run failed' });
          }
          break;
        }
      } catch {
        break;
      }
    }
    set({ screening: false });
    await get().fetchRuns();
  },

  openRun: async (runId) => {
    set({
      activeRun: null,
      results: [],
      resultsTotal: 0,
      resultsPage: 1,
      resultFilter: 'all',
      reviewFilter: 'all',
      resultsSearch: '',
      resultsLoading: true,
    });
    try {
      const run = await amlApi.getScreeningRun(runId);
      set({ activeRun: run });
      await get().fetchResults(1);
    } catch (e: any) {
      set({
        resultsLoading: false,
        error: e?.response?.data?.detail || 'Failed to load screening run',
      });
    }
  },

  closeRun: () => set({ activeRun: null, results: [], resultsTotal: 0 }),

  fetchResults: async (page) => {
    const run = get().activeRun;
    if (!run) return;
    const nextPage = page ?? get().resultsPage;
    set({ resultsLoading: true });
    try {
      const { resultFilter, reviewFilter, resultsSearch, resultsPageSize } = get();
      const { items, total } = await amlApi.listScreeningResults(run.id, {
        page: nextPage,
        page_size: resultsPageSize,
        result: resultFilter === 'all' ? undefined : resultFilter,
        review_status: reviewFilter === 'all' ? undefined : reviewFilter,
        search: resultsSearch || undefined,
      });
      set({
        results: items,
        resultsTotal: total,
        resultsPage: nextPage,
        resultsLoading: false,
      });
    } catch (e: any) {
      set({
        resultsLoading: false,
        error: e?.response?.data?.detail || 'Failed to load screening results',
      });
    }
  },

  setResultFilter: (value) => set({ resultFilter: value, resultsPage: 1 }),
  setReviewFilter: (value) => set({ reviewFilter: value, resultsPage: 1 }),
  setResultsSearch: (value) => set({ resultsSearch: value, resultsPage: 1 }),
  setResultsPageSize: (size) => set({ resultsPageSize: size, resultsPage: 1 }),

  reviewResult: async (resultId, status, notes) => {
    const updated = await amlApi.reviewScreeningResult(resultId, {
      review_status: status,
      review_notes: notes ?? null,
    });
    set({
      results: get().results.map((r) => (r.id === resultId ? updated : r)),
    });
  },
}));
